import React, { useState } from 'react';
import { productClipperAPI, ffeAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link2, Loader2, Sparkles, Plus, CheckCircle2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => typeof n === 'number' && n > 0 ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '';

export default function ProductClipper() {
  const [url, setUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [result, setResult] = useState(null);
  const [enhanced, setEnhanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleScrape = async () => {
    if (!url.trim()) { toast.error('Paste a product URL'); return; }
    setScraping(true); setResult(null); setEnhanced(false); setSaved(false);
    try {
      const res = await productClipperAPI.scrape({ url: url.trim() });
      setResult(res.data);
    } catch (err) { toast.error('Failed to scrape URL'); console.error(err); }
    finally { setScraping(false); }
  };

  const handleEnhance = async () => {
    if (!result) return;
    setEnhancing(true);
    try {
      const res = await productClipperAPI.enhance({ scraped_data: result, url: url.trim() });
      if (res.data.status === 'success') {
        setResult(res.data.data);
        setEnhanced(true);
        toast.success('AI enhanced the product data');
      } else {
        toast.error('AI enhancement failed — using scraped data');
      }
    } catch (err) { toast.error('Enhancement failed'); console.error(err); }
    finally { setEnhancing(false); }
  };

  const handleSaveToFFE = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await ffeAPI.create({
        name: result.name || 'Unnamed Product',
        category: result.category || 'other',
        description: result.description || '',
        dimensions: result.dimensions || '',
        material: result.material || '',
        finish: result.finish || '',
        image_url: result.image_url || '',
        vendor_name: result.vendor_name || '',
        trade_price: result.price || 0,
        markup_percentage: 30,
        status: 'sourcing',
      });
      setSaved(true);
      toast.success('Product saved to FF&E Library');
    } catch (err) { toast.error('Failed to save'); console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="product-clipper-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
            <Link2 className="h-7 w-7 text-muted-foreground" />Product Clipper
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Paste a product URL to auto-extract specs into your FF&E Library</p>
        </div>

        {/* URL Input */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="Paste product URL (e.g., https://www.pepperfry.com/...)"
                  className="pl-10"
                  onKeyDown={e => e.key === 'Enter' && handleScrape()}
                  data-testid="clipper-url-input"
                />
              </div>
              <Button onClick={handleScrape} disabled={scraping} data-testid="clipper-scrape-btn">
                {scraping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
                {scraping ? 'Scraping...' : 'Extract'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <Card data-testid="clipper-result">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Image */}
                {result.image_url ? (
                  <img src={result.image_url} alt={result.name} className="w-full sm:w-48 h-48 object-cover rounded bg-muted" />
                ) : (
                  <div className="w-full sm:w-48 h-48 bg-muted rounded flex items-center justify-center"><ImageIcon className="h-12 w-12 text-muted-foreground/30" /></div>
                )}
                {/* Details */}
                <div className="flex-1 space-y-3">
                  <h3 className="text-lg font-medium">{result.name || 'Unnamed Product'}</h3>
                  {result.price > 0 && <p className="text-2xl font-light text-primary">{fmt(result.price)}</p>}
                  {result.description && <p className="text-sm text-muted-foreground">{result.description}</p>}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {result.dimensions && <div><p className="text-xs text-muted-foreground">Dimensions</p><p>{result.dimensions}</p></div>}
                    {result.material && <div><p className="text-xs text-muted-foreground">Material</p><p>{result.material}</p></div>}
                    {result.finish && <div><p className="text-xs text-muted-foreground">Finish</p><p>{result.finish}</p></div>}
                    {result.category && <div><p className="text-xs text-muted-foreground">Category</p><p className="capitalize">{result.category}</p></div>}
                    {result.vendor_name && <div><p className="text-xs text-muted-foreground">Vendor</p><p>{result.vendor_name}</p></div>}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t">
                {!enhanced && (
                  <Button variant="outline" onClick={handleEnhance} disabled={enhancing} data-testid="clipper-enhance-btn">
                    {enhancing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    {enhancing ? 'Enhancing...' : 'Enhance with AI'}
                  </Button>
                )}
                {enhanced && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />AI Enhanced</span>}
                <Button onClick={handleSaveToFFE} disabled={saving || saved} data-testid="clipper-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  {saved ? 'Saved to FF&E Library' : 'Save to FF&E Library'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
